import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Backdrop,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import axios from "axios";
import { DeletePaymentAPI, GetAllPaymentsAPI } from "../../config/api";
import DeleteIcon from "@material-ui/icons/Delete";
import { ConfirmationDialog } from "../../components/common";
import PaymentAdd from "./PaymentAdd";
import PaymentEdit from "./PaymentEdit";
import EditIcon from "@material-ui/icons/Edit";
import GridItem from "../../components/Grid/GridItem";
import Card from "../../components/Card/Card";
import CardHeader from "../../components/Card/CardHeader";
import CardBody from "../../components/Card/CardBody";
import GridContainer from "../../components/Grid/GridContainer";
import CustomInput from "../../components/CustomInput/CustomInput";
import People from "@material-ui/icons/People";
import _ from "lodash";
import moment from "moment";
import PrintIcon from '@mui/icons-material/Print';

import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { dataURItoBlob, htmlTemplateToDataString } from "../../utils";
import { paymentSlipTemplate } from "../../assets/html/readyOrderTemplate";
import { Search, CloudDownload, CloudUpload } from "@material-ui/icons";
import { ImportExcel } from "../Master/ImportExcel";
import * as XLSX from 'xlsx';
const useStyles = makeStyles((theme) => ({
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
}));

const currencyInr = (value) => {
  const numValue = Number(value);
  if (!Number.isFinite(numValue)) return value ?? "—";
  return numValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    style: "currency",
    currency: "INR",
  });
};

/** Comma-separated challan codes from payment.selectedChallan (various API shapes). */
function formatChallanNumbers(row) {
  // Prefer new backend fields (partial payment support)
  if (row?.challanNo) {
    const s = String(row.challanNo).trim();
    if (s) return s;
  }
  if (Array.isArray(row?.challanPayments) && row.challanPayments.length > 0) {
    const parts = row.challanPayments
      .map((p) => (p?.challanNo != null ? String(p.challanNo).trim() : ""))
      .filter(Boolean);
    if (parts.length) return parts.join(", ");
  }

  const list = row?.selectedChallan;
  if (!Array.isArray(list) || list.length === 0) return "—";
  const parts = list
    .map((c) => {
      if (c == null) return null;
      if (typeof c === "string") return c.trim() || null;
      if (typeof c === "object") {
        return c.code ?? c.challanNo ?? c.challanCode ?? null;
      }
      return null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function isChallanPaymentRow(row) {
  const pf = row?.paymentFor;
  return pf != null && /challan/i.test(String(pf));
}

/**
 * Pending balance for a partially paid challan. Returns null when nothing to show (fully paid or not challan).
 */
function getPendingChallanAmount(row) {
  if (!isChallanPaymentRow(row)) return null;

  // Prefer new backend fields (partial payment support)
  const directPending =
    row?.pendingAmount ?? row?.pendingChallanAmount ?? row?.remainingAmount;
  {
    const n = Number(directPending);
    if (Number.isFinite(n) && n > 0) return n;
  }
  if (Array.isArray(row?.challanPayments) && row.challanPayments.length > 0) {
    // If split across multiple challans, show TOTAL pending after this payment
    const total = row.challanPayments.reduce((sum, p) => {
      const n = Number(p?.pendingAmount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    if (total > 0) return total;
  }

  const status = row?.paymentStatus;
  const isPaid = status === "Paid" || status === "paid";
  if (isPaid) {
    const direct =
      row?.pendingAmount ??
      row?.pendingChallanAmount ??
      row?.remainingAmount;
    const n = Number(direct);
    if (Number.isFinite(n) && n > 0) return n;
    return null;
  }

  const direct =
    row?.pendingAmount ??
    row?.pendingChallanAmount ??
    row?.remainingAmount ??
    row?.balanceAmount;
  let n = Number(direct);
  if (Number.isFinite(n) && n > 0) return n;

  const list = row?.selectedChallan;
  if (Array.isArray(list) && list.length > 0) {
    for (const ch of list) {
      if (!ch || typeof ch !== "object") continue;
      const p =
        ch.pendingAmount ??
        ch.balanceAmount ??
        ch.remainingAmount ??
        (ch.amount != null && ch.paidAmount != null
          ? Number(ch.amount) - Number(ch.paidAmount)
          : null);
      const v = Number(p);
      if (Number.isFinite(v) && v > 0) return v;
    }
  }

  if (status === "Pending" || status === "Partial" || status === "partial") {
    const paidTotal = Array.isArray(row?.payments)
      ? row.payments.reduce((s, m) => s + (Number(m?.paymentAmount) || 0), 0)
      : Number(row?.amount) || 0;
    if (Array.isArray(list) && list[0]) {
      const ch = list[0];
      const challanAmt = Number(ch.amount);
      if (Number.isFinite(challanAmt) && challanAmt > 0) {
        const remaining = challanAmt - paidTotal;
        if (remaining > 0.009) return remaining;
      }
    }
  }

  return null;
}

/** True when this payment is for a challan and the challan is not fully settled (partial payment). */
function isPartialChallanPayment(row) {
  if (!isChallanPaymentRow(row)) return false;
  const p = getPendingChallanAmount(row);
  if (p != null && p > 0) return true;
  const st = row?.paymentStatus;
  return st === "Pending" || st === "Partial" || st === "partial";
}

const columns = [
  { id: "paymentFor", label: "Payment For" },
  { id: "code", label: "Code" },
  { id: "party", label: "Party" },
  {
    id: "paymentDate",
    label: "Date",
    format: (value) => (value ? moment(value).format("DD-MM-YYYY") : "NA"),
  },
  {
    id: "amount", label: "Amount",
    format: (value) =>
      Number(value).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "INR",
      }),
  },
  { id: "challanNo", label: "Challan No.", sortable: false },
  {
    id: "pendingChallanAmount",
    label: "Pending Amount",
    sortable: false,
    format: currencyInr,
  },

  { id: "action", label: "Action" },
];

export default function PaymentList() {
  const classes = useStyles();
  const userInfo = useSelector((state) => state.userReducer.userInfo);

  const authToken = useSelector((state) => state.userReducer.authToken);
  const [payments, setPayments] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [tota, setTota] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    _getPayments(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Memoize the result of the function
  // const memoizedResult = useMemo(() => _getPayments(), []);
  // console.log("here>>>>", memoizedResult)

  async function _getPayments(page, rowsPerPage) {
    setLoading(true);
    // let skipm = page + 2
    try {
      let query = `&limit=${rowsPerPage}&skip=${page * rowsPerPage}&sortBy=createdAt:desc`;
      let res = await axios.get(GetAllPaymentsAPI(userInfo.entity) + query, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      console.log("aaya>>>", res.data)
      setPayments(res.data.payments);
      setTota(res.data.total);
    } catch (err) {
      // console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function _deleteItem(item) {
  try {
      // Server now performs soft delete + side-effects behind this DELETE route
      await axios.delete(DeletePaymentAPI(item._id), {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      _getPayments()
      window.location.reload();
      toast.success("Deleted Successfully")
    } catch (e) {
      console.log("err:", e);
    }
  }


  const handlePrint = async (row) => {
    let options = {
      orientation: "p",
      unit: "pt",
      // format: [400, 800],
      format: 'a4',
      putOnlyUsedFonts: true,
    };
    let htmlTemplate = paymentSlipTemplate(row);
    await htmlTemplateToDataString(htmlTemplate, options, (dataString) => {
      const preBlob = dataURItoBlob(dataString);
      window.open(URL.createObjectURL(preBlob, "_blank"));
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function closeAddDialog() {
    setOpenAddDialog(false);
  }

  function closeEditDialog() {
    setOpenEditDialog(null);
  }

  const handleOpenConfirmationModal = (object) => {
    setOpenConfirmationModal(object);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(null);
  };

  async function searchChallan(searchTerm) {
    setLoading(true);
    try {
      console.log("searchin", searchTerm)
      let query = `&search=${searchTerm}&sortBy=createdAt: desc`;
      let res = await axios.get(GetAllPaymentsAPI(userInfo.entity) + query, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setPayments(res.data.payments);
      setTota(res.data.total);
      // Process the result of the API call
    } catch (error) {
      console.error("Error fetching data from API:", error);
    } finally {
      setLoading(false);
    }
  }

  let data = payments;
  // if (searchTerm !== "") {
  //   data = _.filter(data, (i) => {
  //     return i.code && i.code.toLowerCase().includes(searchTerm.toLowerCase());
  //   });
  // }

  useEffect(() => {
    if (searchTerm !== "") {
      console.log("search>>>", searchTerm)
      searchChallan(searchTerm);
    } else {
      _getPayments(page, rowsPerPage)
    }
  }, [searchTerm]);

  function handleSearch() {
    setSearchTerm(search);
  }

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPayments = React.useMemo(() => {
    let sortablePayments = [...payments];
    if (sortConfig.key) {
      sortablePayments.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortablePayments;
  }, [payments, sortConfig]);

  // Export function
  const handleExport = async () => {
    setIsExporting(true);
    const loadingToastId = toast.loading('🔄 Preparing export... Please wait', {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });

    try {
      // First, get the total count
      toast.update(loadingToastId, {
        render: '📊 Fetching total record count...',
        type: 'info',
        isLoading: true,
      });

      const totalRes = await axios.get(GetAllPaymentsAPI(userInfo.entity) + `&limit=1&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const total = totalRes.data.total || 0;
      
      if (total === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      // Fetch all data using the total count
      toast.update(loadingToastId, {
        render: `📥 Downloading ${total} records... This may take a moment`,
        type: 'info',
        isLoading: true,
      });

      const res = await axios.get(GetAllPaymentsAPI(userInfo.entity) + `&limit=${total}&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const allData = res.data.payments;
      if (!allData || allData.length === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      toast.update(loadingToastId, {
        render: '⚙️ Processing data for export...',
        type: 'info',
        isLoading: true,
      });

      const exportHeadings = [
        { id: "code", label: "Code" },
        { id: "paymentFor", label: "Payment For" },
        { id: "party", label: "Party" },
        { id: "paymentDate", label: "Payment Date" },
        { id: "amount", label: "Amount" },
        { id: "paymentMode", label: "Payment Mode" },
        { id: "bank", label: "Bank" },
        { id: "chequeNumber", label: "Cheque Number" },
        { id: "chequeDate", label: "Cheque Date" },
        { id: "remarks", label: "Remarks" }
      ];

      const exportData = allData.map(row => {
        const rowData = {};
        exportHeadings.forEach(col => {
          let value = row[col.id];
          if (col.id === 'party' && value && value.name) {
            value = value.name;
          }
          if (col.id === 'bank' && value && value.bankName) {
            value = value.bankName;
          }
          if (col.id === 'paymentDate' && value) {
            value = moment(value).format("DD-MM-YYYY");
          }
          if (col.id === 'chequeDate' && value) {
            value = moment(value).format("DD-MM-YYYY");
          }
          rowData[col.id] = value;
        });
        return rowData;
      });

      toast.update(loadingToastId, {
        render: '💾 Generating Excel file...',
        type: 'info',
        isLoading: true,
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
      XLSX.writeFile(workbook, 'payments_export.xlsx');

      toast.update(loadingToastId, {
        render: `✅ Successfully exported ${allData.length} records to Excel!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.update(loadingToastId, {
        render: `❌ Export failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        style: {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <Box
              style={{
                display: "flex",
                flex: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 className={classes.cardTitleWhite}>Payments</h4>
              <Box
                style={{
                  display: "flex",
                  flex: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomInput
                  id="search"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    style: {
                      color: "white",
                      marginTop: 0,
                    },
                    placeholder: "Search",
                    value: search,
                    // onChange: (e) => setSearch(e.target.value),
                    onChange: (e) => {
                      setSearch(e.target.value);
                      if (e.target.value === "" || e.target.value === null) {
                        _getPayments(page, rowsPerPage)
                      }

                    },
                    // onChange: (e) => handleInputChange(e),
                    onKeyDown: (e) => {
                      if (e.keyCode === 13) {
                        e.preventDefault();
                        handleSearch();
                      }
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <People />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton color={"secondary"} onClick={(e) => {
                  // setSearchTerm(e.target.value),
                  // handleKeyPress(e)
                  // handleKeyPress()
                  handleSearch()


                }}>
                  <Search />
                </IconButton>
                <Tooltip title={"Export to excel"}>
                  <IconButton
                    color={"primary"}
                    onClick={handleExport}
                    disabled={isExporting}
                    style={{ color: "white" }}
                  >
                    <CloudDownload />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Import Excel to database"}>
                  <IconButton
                    color={"primary"}
                    onClick={() => setOpenImportDialog(true)}
                    style={{ color: "white" }}
                  >
                    <CloudUpload />
                  </IconButton>
                </Tooltip>
                <IconButton
                  aria-label="add"
                  style={{ color: "white" }}
                  onClick={() => setOpenAddDialog(true)}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </CardHeader>
          <CardBody>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => 
                  {return (
                    <Tooltip
                      key={column.id}
                      title={column.sortable === false ? "" : `Sort by ${column.id}`}
                    >
                    <TableCell
                      key={column.id}
                      align={"center"}
                      onClick={column.sortable === false ? undefined : () => requestSort(column.id)}
                      style={{
                        minWidth: column.minWidth,
                        cursor: column.sortable === false ? "default" : "pointer",
                      }}
                    >
                      {column.label}
                    </TableCell>
                    </Tooltip>
                  )})}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                      <Box style={{ color: "#666", marginTop: "16px" }}>
                        {searchTerm ? "Searching payments..." : "Loading payments..."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : sortedPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <Box style={{ color: "#666" }}>
                        {searchTerm ? "No payments found matching your search." : "No payments found."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedPayments
                  // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.code}
                      >
                        {columns.map((column) => {
                          let value = row[column.id];
                          if (column.id === "challanNo") {
                            value = isPartialChallanPayment(row)
                              ? formatChallanNumbers(row)
                              : "—";
                          }
                          if (column.id === "pendingChallanAmount") {
                            const pend = getPendingChallanAmount(row);
                            value =
                              isPartialChallanPayment(row) &&
                              pend != null &&
                              pend > 0
                                ? pend
                                : "—";
                          }
                          if (column.id === "party") {
                            value = row.party.name;
                          }
                          if (column.id === "action") {
                            return (
                              <TableCell align={"center"}>
                                <Box display={"flex"} justifyContent={"center"}>
                                  {/* <IconButton
                                    aria-label="delete"
                                    onClick={() =>
                                      setOpenEditDialog({
                                        open: true,
                                        item: row,
                                      })
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton> */}
                                  <IconButton
                                    aria-label="view"
                                    onClick={() =>
                                      handlePrint(row)
                                    }
                                  >
                                    <PrintIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="delete"
                                    onClick={() =>
                                      handleOpenConfirmationModal({
                                        onPress: () => _deleteItem(row),
                                      })
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={"center"}>
                              {value === "—"
                                ? "—"
                                : column.format
                                  ? column.format(value)
                                  : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={tota}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardBody>
        </Card>
      </GridItem>

      {/*<Fab color="primary" aria-label="add" className={classes.fab}*/}
      {/*     onClick={() => setOpenAddDialog(true)}>*/}
      {/*    <AddIcon/>*/}
      {/*</Fab>*/}
      <PaymentAdd
        fetchData={_getPayments}
        open={openAddDialog}
        close={closeAddDialog}
      />
      {openEditDialog ? (
        <PaymentEdit
          fetchData={_getPayments}
          open={openEditDialog.open}
          item={openEditDialog.item}
          close={closeEditDialog}
        />
      ) : null}
      {openConfirmationModal ? (
        <ConfirmationDialog
          open={openConfirmationModal}
          onClose={handleCloseConfirmationModal}
          onConfirm={openConfirmationModal.onPress}
          title={"Delete Record"}
          msg={"Do you want to delete this record?"}
        />
      ) : null}
      <ImportExcel
        db={"payments"}
        open={openImportDialog}
        close={() => {
          setOpenImportDialog(false);
          _getPayments(page, rowsPerPage);
        }}
      />
      <Backdrop
        open={isExporting}
        style={{
          zIndex: 9999,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={60} thickness={4} style={{ color: '#512da8', marginBottom: 20 }} />
          <Box style={{ fontSize: '18px', fontWeight: 'bold', marginTop: 10 }}>
            Exporting Data...
          </Box>
          <Box style={{ fontSize: '14px', marginTop: 5, opacity: 0.8 }}>
            Please wait while we prepare your Excel file
          </Box>
        </Box>
      </Backdrop>
    </GridContainer>
  );
}
